import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  UserIcon, 
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ShareIcon,
  PrinterIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import aedIcon from '../assets/aed.svg';
import logo from '../assets/logo.png';
import { getBooking, BookingData as ApiBookingData } from '../utils/bookingService';
import { getDiscountedPrice } from '../config/pricing';

type BookingData = ApiBookingData;

const BookingConfirmation: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (bookingId) {
      loadBookingData(bookingId);
    } else {
      setError('No booking ID provided');
      setLoading(false);
    }
  }, [bookingId]);

  const loadBookingData = async (id: string) => {
    try {
      console.log('Loading booking data for ID (backend first):', id);

      // 1) Fetch from backend API
      const apiResult = await getBooking(id);
      if (apiResult) {
        setBooking(apiResult);
        return;
      }

      // 2) Fallback: URL parameters (legacy flow)
      const urlParams = new URLSearchParams(window.location.search);
      const bookingDataParam = urlParams.get('data');
      if (bookingDataParam) {
        const bookingData = JSON.parse(decodeURIComponent(bookingDataParam));
        const existingBookings = localStorage.getItem('bookings');
        const bookings: BookingData[] = existingBookings ? JSON.parse(existingBookings) : [];
        const filteredBookings = bookings.filter(b => b.id !== id);
        filteredBookings.push(bookingData);
        localStorage.setItem('bookings', JSON.stringify(filteredBookings));
        setBooking(bookingData);
        return;
      }

      // 3) Fallback: localStorage (legacy backup)
      const storedBookings = localStorage.getItem('bookings');
      if (storedBookings) {
        const bookings: BookingData[] = JSON.parse(storedBookings);
        const foundBooking = bookings.find(b => b.id === id);
        if (foundBooking) {
          setBooking(foundBooking);
          return;
        }
      }

      setError('Booking not found.');
    } catch (err) {
      console.error('Error loading booking data:', err);
      setError('Error loading booking data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const getPackagePrice = (packageName: string) => {
    const discounted = getDiscountedPrice(packageName);
    if (discounted === 'Quote') {
      return 'Quote on request';
    }
    return discounted;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Booking Confirmation - Book My Shoot',
          text: `Your photography session is confirmed for ${booking?.date} at ${booking?.time}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Booking confirmation link copied to clipboard!');
    }
  };

  const getTermsAndConditions = () => [
    {
      title: "Payment Terms",
      content: "Full payment is required 48 hours before the session. Payment can be made via bank transfer, credit card, or cash. Late payments may result in session cancellation."
    },
    {
      title: "Cancellation Policy",
      content: "Cancellations made 48+ hours in advance: Full refund. 24-48 hours: 50% refund. Less than 24 hours: No refund. Weather-related cancellations will be rescheduled at no additional cost."
    },
    {
      title: "Rescheduling",
      content: "One free reschedule allowed if requested 48+ hours in advance. Additional reschedules may incur a 25% fee. Last-minute changes (less than 24 hours) may not be accommodated."
    },
    {
      title: "Session Duration",
      content: "Session duration is as specified in your package. Additional time beyond the agreed duration will be charged at AED 200 per hour. Setup and breakdown time is included in your session time."
    },
    {
      title: "Photo Delivery",
      content: "Edited photos will be delivered within 14-21 business days via online gallery. Raw files are not included unless specifically requested and paid for. High-resolution files are provided for personal use only."
    },
    {
      title: "Usage Rights",
      content: "Book My Shoot retains the right to use photos for marketing purposes unless otherwise agreed. Client receives personal usage rights. Commercial usage requires additional licensing agreement."
    },
    {
      title: "Weather Policy",
      content: "Outdoor sessions may be rescheduled due to severe weather. Light rain or overcast conditions may proceed. Final decision on weather-related cancellations is at photographer's discretion."
    },
    {
      title: "Liability",
      content: "Book My Shoot is not liable for equipment damage, personal injury, or property damage during the session. Client is responsible for securing necessary permits for location photography."
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The booking you\'re looking for doesn\'t exist or has been removed.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header */}
      <header className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src={logo} 
                alt="Book My Shoot" 
                className="h-8 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span className="text-xl font-bold text-gray-900">Book My Shoot</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePrint}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <PrinterIcon className="w-4 h-4 mr-2" />
                Print
              </button>
              <button
                onClick={handleShare}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ShareIcon className="w-4 h-4 mr-2" />
                Share
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Confirmation Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <CheckCircleIcon className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Booking Confirmed!</h1>
          <p className="text-xl text-gray-600 mb-6">Your photography session has been successfully booked</p>
          <div className="inline-flex items-center px-6 py-3 bg-green-100 text-green-800 rounded-full text-lg font-medium">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
            Confirmed
          </div>
        </motion.div>

        {/* Booking Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gray-50 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
            <DocumentTextIcon className="w-8 h-8 mr-3 text-orange-500" />
            Session Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <UserIcon className="w-6 h-6 text-gray-400 mt-1 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Client Name</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{booking.name}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <EnvelopeIcon className="w-6 h-6 text-gray-400 mt-1 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Email</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{booking.email}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <PhoneIcon className="w-6 h-6 text-gray-400 mt-1 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Phone</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{booking.phone}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <CalendarIcon className="w-6 h-6 text-gray-400 mt-1 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Date</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{formatDate(booking.date)}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <ClockIcon className="w-6 h-6 text-gray-400 mt-1 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Time</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{formatTime(booking.time)}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <MapPinIcon className="w-6 h-6 text-gray-400 mt-1 mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Location</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{booking.location}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Package Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-orange-50 rounded-2xl p-8 mb-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Package Information</h3>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{booking.package}</p>
              <p className="text-lg text-gray-600 mt-2">{booking.eventType}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-orange-600 flex items-center">
                <img src={aedIcon} alt="AED" className="w-6 h-6 mr-2" />
                {getPackagePrice(booking.package)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Additional Details */}
        {booking.details && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white border border-gray-200 rounded-2xl p-8 mb-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Additional Details</h3>
            <p className="text-lg text-gray-700 leading-relaxed">{booking.details}</p>
          </motion.div>
        )}

        {/* Booking Reference */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-gray-900 text-white rounded-2xl p-8 mb-8"
        >
          <h3 className="text-2xl font-bold mb-6">Booking Reference</h3>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-sm text-gray-300 mb-2">Booking ID</p>
            <p className="text-2xl font-mono font-bold">{booking.id}</p>
          </div>
          <p className="text-gray-300 mt-4">
            Keep this reference number for your records. You'll need it for any future communications about this booking.
          </p>
        </motion.div>

        {/* Terms and Conditions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white border border-gray-200 rounded-2xl p-8 mb-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
            <ExclamationTriangleIcon className="w-8 h-8 mr-3 text-orange-500" />
            Terms and Conditions
          </h3>
          
          <div className="space-y-8">
            {getTermsAndConditions().map((term, index) => (
              <div key={index} className="border-l-4 border-orange-200 pl-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{term.title}</h4>
                <p className="text-gray-700 leading-relaxed">{term.content}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-blue-50 rounded-2xl p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <EnvelopeIcon className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-lg font-semibold text-gray-900">info@bookmyshot.com</p>
              </div>
            </div>
            <div className="flex items-center">
              <PhoneIcon className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-lg font-semibold text-gray-900">+971 XX XXX XXXX</p>
              </div>
            </div>
          </div>
          <div className="mt-6 p-4 bg-blue-100 rounded-lg">
            <p className="text-blue-800">
              <strong>Important:</strong> We'll contact you within 24 hours to confirm all details and answer any questions about your photography session.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default BookingConfirmation;
